import React, { ComponentType } from 'react';
import { Box, BoxProps } from '@cp/ds/src/components/box';
import { styled } from '@cp/ds/src/theme';
// import { coreCareerApi } from '@cp/entities/career';
import { Image as Img, TypographyAccent } from '@youhelp-ru/ui';
import { Typography } from '@mui/material';
import { CareerViewModel } from '@common/model/catalog/career';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { ROUTES_DEFAULT_CITY_ALIAS } from '@common/const/routes/defaultCity';
// import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { useCurrentCity } from '@cp/entities/location';
import { useIsMobile, useTheme } from '@cp/ds/src/hooks';
import { NannyIcon } from '@cp/ds/src/icons/career/colored/nanny';
// import { NurseIcon } from '@cp/ds/src/icons/career/colored/nurse';
// import { TutorIcon } from '@cp/ds/src/icons/career/colored/tutor';
// import { HousekeeperIcon } from '@cp/ds/src/icons/career/colored/housekeeper';
import { DriverIcon } from '@cp/ds/src/icons/driver';
import { SvgIconProps } from '@cp/ds/src/icons';
import { RoutedLink } from 'src/shared/ui/link';
import VectorVertical from 'src/assets/shapes/vectors/vectorVertical.svg';
import BannerImage from 'src/assets/images/showcaseBanner.png';
import BannerImage_webp from 'src/assets/images/showcaseBanner.webp';

const name = 'ShowcaseGrid';

const Root = styled(Box, { name })(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridAutoFlow: 'row dense',
  gridAutoRows: 'minmax(116px, auto)',
  alignItems: 'stretch',
  minHeight: '500px',
  gap: theme.spacing(1.5),
  paddingBottom: theme.spacing(2.5),
  '@container (min-width: 900px)': {
    paddingBottom: theme.spacing(7.5),
    gap: theme.spacing(2.5),
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
})) as unknown as ComponentType<BoxProps>;

const Item = styled(Box, { name, slot: 'item' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  width: '100%',
  height: '100%',
  gridColumn: 'span 1',
  gridRow: 'span 1',
  borderRadius: '16px',
  padding: theme.spacing(1.5),
  [theme.breakpoints.up('md')]: {
    padding: '28px 20px',
  },
})) as unknown as ComponentType<BoxProps>;

const IconWrapper = styled(Box, { name, slot: 'item' })(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: 36,
  minHeight: 36,
  backgroundColor: theme.palette.white,
  borderRadius: '50%',
  padding: theme.spacing(1.5),
  [theme.breakpoints.up('md')]: {
    minWidth: 60,
    minHeight: 60,
  },
})) as unknown as ComponentType<BoxProps>;

const BannerItem = styled(Box, { name, slot: 'item' })({
  width: '100%',
  height: '100%',
  display: 'block',
  gridColumn: 'span 2',
  gridRow: 'span 2',
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'relative',
}) as unknown as ComponentType<BoxProps>;

interface ShowcaseItemProps extends BoxProps {
  career: CareerViewModel;
  mode: CatalogItemType;
  icon: (props: SvgIconProps) => React.ReactNode;
}

const Image = styled(Img)({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const Banner: React.FC = () => {
  return (
    <BannerItem>
      <Box
        sx={{
          color: 'white',
          position: 'absolute',
          bottom: '-7px',
          left: 0,
          width: '9%',
        }}
      >
        <VectorVertical />
      </Box>
      <picture>
        <source srcSet={BannerImage_webp} type="image/webp" />
        <source srcSet={BannerImage} type="image/png" />
        <Image alt="nanny" loading="lazy" src={BannerImage} />
      </picture>
    </BannerItem>
  );
};

const ShowcaseItem: React.FC<ShowcaseItemProps> = ({ career, mode, icon, ...props }) => {
  const Icon = icon;

  return (
    <Item {...props}>
      <IconWrapper>
        <Icon
          sx={{
            fontSize: { xs: '1.5rem', md: '2.25rem' },
            color: 'gray',
          }}
        />
      </IconWrapper>
      <Typography sx={{ typography: { xs: 'h3', md: 'h2' } }} variant="h2">
        {mode === CatalogItemType.Resume ? 'Проверенные' : 'Вакансии для'}
        <br />
        <TypographyAccent>
          {mode === CatalogItemType.Resume ? career.translation.namePlural : career.translation.nameDeclination2}
        </TypographyAccent>
      </Typography>
    </Item>
  );
};

/** Labels for hardcoded nanny subcatalog tiles (YOU-5106) */
const label = (namePlural: string, nameDeclination2: string) =>
  ({ translation: { namePlural, nameDeclination2 } }) as CareerViewModel;

/** /nannies/ or /spb/nannies/ — moscow (default) without prefix */
const withCity = (path: string, cityAlias?: string) => {
  if (!cityAlias || cityAlias === ROUTES_DEFAULT_CITY_ALIAS) {
    return path;
  }
  return `/${cityAlias}${path}`;
};

export interface ShowcaseGridProps extends BoxProps {}
export const ShowcaseGrid: React.FC<ShowcaseGridProps> = () => {
  // const { data: careerDictionary } = coreCareerApi.endpoints.getCareers.useQuery();
  // const routeResolver = useClientWebResolver();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const isMobile = useIsMobile();
  const theme = useTheme();

  // const icons = [NannyIcon, NurseIcon, TutorIcon, HousekeeperIcon];
  const itemSx = { backgroundColor: theme.palette['gray ultra light'] };
  const href = (path: string) => withCity(path, currentCity?.alias);

  return (
    <Root>
      {/* YOU-5106: when other specialties are back — uncomment this map (+ imports/hooks above), comment hardcoded resume links
      {Object.entries({ ...careerDictionary }).map(([careerId, model], index) => (
        <RoutedLink
          href={routeResolver.resolve({
            page: ClientWebPage.Catalog,
            pageProps: { catalogType: CatalogItemType.Resume, careerId: model.id },
            cityAlias: currentCity?.alias,
          })}
          key={careerId}
          sx={{ display: 'contents' }}
          underline={false}
        >
          <ShowcaseItem
            career={model}
            icon={icons[index]}
            mode={CatalogItemType.Resume}
            sx={{
              backgroundColor: theme.palette['gray ultra light'],
            }}
          />
        </RoutedLink>
      ))}
      */}

      <RoutedLink href={href('/nannies/')} sx={{ display: 'contents' }} underline={false}>
        <ShowcaseItem career={label('няни', 'няни')} icon={NannyIcon} mode={CatalogItemType.Resume} sx={itemSx} />
      </RoutedLink>
      <RoutedLink href={href('/babysitters/')} sx={{ display: 'contents' }} underline={false}>
        <ShowcaseItem career={label('бебиситтеры', 'бебиситтера')} icon={NannyIcon} mode={CatalogItemType.Resume} sx={itemSx} />
      </RoutedLink>
      <RoutedLink href={href('/car-nannies/')} sx={{ display: 'contents' }} underline={false}>
        <ShowcaseItem career={label('автоняни', 'автоняни')} icon={DriverIcon} mode={CatalogItemType.Resume} sx={itemSx} />
      </RoutedLink>
      <RoutedLink href={href('/nannies-for-an-hour/')} sx={{ display: 'contents' }} underline={false}>
        <ShowcaseItem career={label('няни на час', 'няни на час')} icon={NannyIcon} mode={CatalogItemType.Resume} sx={itemSx} />
      </RoutedLink>

      {!isMobile && <Banner />}

      {/* YOU-5106: when other specialties are back — uncomment this map, comment hardcoded vacancy links
      {Object.entries({ ...careerDictionary }).map(([careerId, model], index) => (
        <RoutedLink
          href={routeResolver.resolve({
            page: ClientWebPage.Catalog,
            pageProps: { catalogType: CatalogItemType.Vacancy, careerId: model.id },
            cityAlias: currentCity?.alias,
          })}
          key={careerId}
          sx={{ display: 'contents' }}
          underline={false}
        >
          <ShowcaseItem
            career={model}
            icon={icons[index]}
            mode={CatalogItemType.Vacancy}
            sx={{
              backgroundColor: theme.palette['gray ultra light'],
            }}
          />
        </RoutedLink>
      ))}
      */}

      <RoutedLink href={href('/work-for-nannies/')} sx={{ display: 'contents' }} underline={false}>
        <ShowcaseItem career={label('няни', 'няни')} icon={NannyIcon} mode={CatalogItemType.Vacancy} sx={itemSx} />
      </RoutedLink>
      <RoutedLink href={href('/work-for-nannies-on-time/')} sx={{ display: 'contents' }} underline={false}>
        <ShowcaseItem career={label('няни на час', 'няни на час')} icon={NannyIcon} mode={CatalogItemType.Vacancy} sx={itemSx} />
      </RoutedLink>
      <RoutedLink href={href('/work-for-car-nanny/')} sx={{ display: 'contents' }} underline={false}>
        <ShowcaseItem career={label('автоняни', 'автоняни')} icon={DriverIcon} mode={CatalogItemType.Vacancy} sx={itemSx} />
      </RoutedLink>
      <RoutedLink href={href('/work-for-babysitter/')} sx={{ display: 'contents' }} underline={false}>
        <ShowcaseItem career={label('бебиситтеры', 'бебиситтера')} icon={NannyIcon} mode={CatalogItemType.Vacancy} sx={itemSx} />
      </RoutedLink>

      {isMobile && <Banner />}
    </Root>
  );
};
